import { useState, useMemo } from 'react';

/**
 * useSortableData — generic sort hook for tables.
 * Cycle: unsorted → asc → desc → unsorted (when same key clicked).
 *
 * @param items array of records
 * @param defaultKey optional initial sort key
 * @param defaultDir optional initial sort direction ('asc' | 'desc')
 * @returns { sortedItems, sortKey, sortDir, requestSort, getSortIndicator }
 */
export const useSortableData = (items, defaultKey = null, defaultDir = 'asc') => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const requestSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // 3rd click on same column → clear sort
    setSortKey(null);
    setSortDir('asc');
  };

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const arr = [...(items || [])];

    const getValue = (obj, key) => {
      if (!obj) return null;
      // support dotted keys
      const v = key.split('.').reduce((acc, k) => (acc ? acc[k] : undefined), obj);
      return v;
    };

    const isNumericLike = (v) => {
      if (v === null || v === undefined || v === '') return false;
      if (typeof v === 'number') return true;
      const n = Number(String(v).replace(/[^\d.\-]/g, ''));
      return !Number.isNaN(n) && Number.isFinite(n);
    };

    arr.sort((a, b) => {
      let av = getValue(a, sortKey);
      let bv = getValue(b, sortKey);

      // null / undefined go last regardless of direction
      if (av === null || av === undefined || av === '') return 1;
      if (bv === null || bv === undefined || bv === '') return -1;

      // Date heuristic
      const isDateKey = /date|_at|expir|warranty/i.test(sortKey);
      if (isDateKey) {
        const da = new Date(av).getTime();
        const db = new Date(bv).getTime();
        if (!Number.isNaN(da) && !Number.isNaN(db)) {
          return sortDir === 'asc' ? da - db : db - da;
        }
      }

      // Numeric (including currency-like strings)
      if (isNumericLike(av) && isNumericLike(bv)) {
        const na = typeof av === 'number' ? av : Number(String(av).replace(/[^\d.\-]/g, ''));
        const nb = typeof bv === 'number' ? bv : Number(String(bv).replace(/[^\d.\-]/g, ''));
        return sortDir === 'asc' ? na - nb : nb - na;
      }

      // Fallback: string compare (locale + numeric for natural ordering: 2 < 10)
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return arr;
  }, [items, sortKey, sortDir]);

  const getSortIndicator = (key) => {
    if (sortKey !== key) return '↕';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  return { sortedItems, sortKey, sortDir, requestSort, getSortIndicator };
};
